/**
 * Automações do Instagram — "quem comentar X recebe Y no privado".
 *
 * Uma automação é presa a um post (ou a qualquer post, ou ao próximo reel),
 * tem palavras-chave e uma mensagem com botão de link rastreado. O que a
 * pessoa vê aqui: a lista com os números (enviadas, cliques), o editor, e o
 * log de entregas — "mandou para quem, quando, e por que não".
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import type { AutomationRow } from "@/lib/instagram-automation-form";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Instagram,
  Plus,
  Zap,
  Pencil,
  Trash2,
  ExternalLink,
  Loader2,
  MousePointerClick,
  Send,
  ListChecks,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { tempoRelativo } from "@/lib/tempo";
import { INSTAGRAM_AUTOMATION_TEMPLATES } from "@shared/instagram-automation-templates";

// ─── Log ─────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  sent: { label: "Enviada", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Aguardando", cls: "bg-amber-100 text-amber-800" },
  failed: { label: "Falhou", cls: "bg-red-100 text-red-800" },
  skipped_dedup: { label: "Já respondido", cls: "bg-slate-100 text-slate-700" },
  skipped_rate_limit: { label: "Limite/hora", cls: "bg-slate-100 text-slate-700" },
};

function Log({ automationId, nome, onFechar }: { automationId: number; nome: string; onFechar: () => void }) {
  const logs = trpc.instagram.automations.logs.useQuery({ automationId, limit: 100 }, { refetchInterval: 30_000 });
  return (
    <Sheet open onOpenChange={(o) => !o && onFechar()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Entregas — {nome}</SheetTitle>
          <SheetDescription>Quem recebeu, quando, e por que não recebeu.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {logs.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {logs.data?.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma entrega ainda.</p>}
          {logs.data?.map((l) => {
            const s = STATUS_LABEL[l.status] ?? { label: l.status, cls: "" };
            return (
              <div key={l.id} className="rounded border p-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{l.commenterUsername ? `@${l.commenterUsername}` : "(sem @)"}</span>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {l.publicReplySentAt && <Badge variant="outline" className="text-[10px]">resp. pública</Badge>}
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${s.cls}`}>{s.label}</span>
                    <span>{tempoRelativo(l.dmSentAt ?? l.createdAt)}</span>
                  </div>
                </div>
                {l.commentText && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">“{l.commentText}”</p>}
                {l.matchedKeyword && <p className="mt-0.5 text-[11px] text-muted-foreground">palavra: {l.matchedKeyword}</p>}
                {l.errorMessage && <p className="mt-1 text-xs text-red-600">{l.errorMessage}</p>}
                {l.publicReplyError && <p className="mt-1 text-xs text-amber-700">Resposta pública: {l.publicReplyError}</p>}
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

function InstagramAutomationsContent() {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const podeGerenciar = can("instagram:manage_automations");

  const status = trpc.instagram.automations.status.useQuery(undefined, { staleTime: 30_000 });
  const lista = trpc.instagram.automations.list.useQuery(undefined, { staleTime: 15_000, refetchInterval: 60_000 });

  const [, navigate] = useLocation();
  const [log, setLog] = useState<{ id: number; nome: string } | null>(null);
  const [excluindo, setExcluindo] = useState<AutomationRow | null>(null);
  const [mostrarModelos, setMostrarModelos] = useState(false);

  const invalidar = () => void utils.instagram.automations.list.invalidate();

  const alternar = trpc.instagram.automations.setActive.useMutation({
    onSuccess: (r) => { invalidar(); toast.success(r.isActive ? "Automação ativada." : "Automação pausada."); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });
  const excluir = trpc.instagram.automations.delete.useMutation({
    onSuccess: () => { invalidar(); setExcluindo(null); toast.success("Automação excluída."); },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  if (status.isFetched && status.data && !status.data.conectado) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <Instagram className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 text-lg font-semibold">Instagram não conectado</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Conecte a conta em <strong>Configurações → Integrações → Instagram</strong> para criar automações de comentário.
        </p>
      </div>
    );
  }

  const uso = status.data?.limiteHora;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Automações do Instagram</h1>
          <p className="text-sm text-muted-foreground">
            Quem comentar a palavra-chave recebe a mensagem no privado — com link rastreado, sem ninguém responder à mão.
          </p>
        </div>
        {podeGerenciar && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMostrarModelos((v) => !v)}>
              <Sparkles className="mr-2 h-4 w-4" /> Modelos
            </Button>
            <Button onClick={() => navigate("/instagram/automations/new")}>
              <Plus className="mr-2 h-4 w-4" /> Nova automação
            </Button>
          </div>
        )}
      </div>

      {status.data && !status.data.linksRastreadosProntos && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          A URL pública do sistema não está configurada (PUBLIC_BASE_URL). Os links rastreados sairão relativos e não vão abrir — configure antes de ativar automações com link.
        </p>
      )}

      {uso && uso.used > uso.max * 0.8 && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          {uso.used} de {uso.max} DMs na última hora. Perto do limite do Instagram: o que passar volta para a fila em 30 min.
        </p>
      )}

      {mostrarModelos && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {INSTAGRAM_AUTOMATION_TEMPLATES.map((t) => (
            <Card key={t.slug} className="cursor-pointer transition hover:border-primary" onClick={() => navigate(`/instagram/automations/new?modelo=${t.slug}`)}>
              <CardContent className="p-4">
                <p className="text-[11px] uppercase text-muted-foreground">{t.category}</p>
                <p className="font-medium">{t.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.summary}</p>
                <div className="mt-2 flex flex-wrap gap-1">{t.keywords.map((k) => <Badge key={k} variant="secondary" className="text-[10px]">{k}</Badge>)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {lista.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {lista.isError && <p className="text-sm text-red-600">{toUserFriendlyError(lista.error)}</p>}
      {lista.data?.length === 0 && (
        <div className="rounded border border-dashed py-12 text-center">
          <Zap className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">Nenhuma automação ainda. Comece por um modelo.</p>
        </div>
      )}

      <div className="space-y-3">
        {lista.data?.map((a) => {
          const alvo = a.pendingNextReel ? "Próximo reel (aguardando)" : a.matchAnyPost ? "Qualquer post" : a.mediaId ? "Post específico" : "Só por DM";
          const ctr = a.stats.sent > 0 ? Math.round((a.stats.clicks / a.stats.sent) * 100) : null;
          return (
            <Card key={a.id} className={a.isActive ? "" : "opacity-70"}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{a.name}</p>
                      <Badge variant={a.isActive ? "default" : "outline"}>{a.isActive ? "Ativa" : "Pausada"}</Badge>
                      <Badge variant="secondary">{alvo}</Badge>
                      {a.dmTriggerEnabled && <Badge variant="secondary">+ DM</Badge>}
                      {a.requireFollow && <Badge variant="secondary">exige seguir</Badge>}
                      {a.mediaPermalink && (
                        <a href={a.mediaPermalink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          <ExternalLink className="h-3 w-3" /> ver post
                        </a>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {a.matchAnyWord ? <Badge variant="outline" className="text-[10px]">qualquer comentário</Badge> : a.keywords.map((k) => <Badge key={k} variant="outline" className="text-[10px]">{k}</Badge>)}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Send className="h-3 w-3" /> {a.stats.sent} enviadas</span>
                      {a.trackedLinks.length > 0 && (
                        <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" /> {a.stats.clicks} cliques{ctr != null && ` (${ctr}%)`}</span>
                      )}
                      {a.stats.publicReplies > 0 && <span>{a.stats.publicReplies} resp. públicas</span>}
                      {a.stats.failed > 0 && <span className="text-red-600">{a.stats.failed} falhas</span>}
                      {a.stats.skipped > 0 && <span>{a.stats.skipped} puladas</span>}
                      {a.stats.lastSentAt && <span>última {tempoRelativo(a.stats.lastSentAt)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setLog({ id: a.id, nome: a.name })} title="Entregas">
                      <ListChecks className="h-4 w-4" />
                    </Button>
                    {podeGerenciar && (
                      <>
                        <Switch checked={a.isActive} onCheckedChange={(v) => alternar.mutate({ id: a.id, isActive: v })} disabled={alternar.isPending} />
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/instagram/automations/${a.id}`)} title="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setExcluindo(a)} title="Excluir">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {log && <Log automationId={log.id} nome={log.nome} onFechar={() => setLog(null)} />}

      <AlertDialog open={!!excluindo} onOpenChange={(o) => !o && setExcluindo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir “{excluindo?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              O histórico de entregas e os cliques desta automação são apagados junto. Para só parar, use “Pausar”.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => excluindo && excluir.mutate({ id: excluindo.id })} disabled={excluir.isPending}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function InstagramAutomations() {
  return (
    <PermissionGuard permission="instagram:view_comments">
      <DashboardLayout>
        <InstagramAutomationsContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
